import { Children, cloneElement, useCallback, useMemo } from "react";
import { useUIDSeed } from "react-uid";
import styled from "@emotion/styled";
import PropTypes from "prop-types";

import {
    DRAWER_ACTIONS,
    useDrawerDispatch,
    useDrawerState,
} from "@hmn/coolinarika-web-core/context/Drawer";
import {
    InfiniteLoadTrigger,
    useListElastic,
    useQueryState,
    useUser,
} from "@hmn/coolinarika-web-core/hooks";

import { Button, buttonSizes, buttonVariants } from "../Button";
import { DrawerBody, DrawerFooter } from "../Drawer";
import { withErrorBoundary } from "../ErrorBoundary";
import { CheckMarkIcon, Icon } from "../Icon";
// Internal Uploader components
import { Notification, UploaderGalleryImage, UploaderGalleryImageAdd } from "./components";
import styles from "./Gallery.style";

const UploaderGalleryStyled = styled.div((props) => ({ ...styles(props) }));

const NOTIFICATION_ID = "PLACEHOLDER_ID_FOR_NOTIFICATION";

const drawerUID = "drawer-image-uploader-editor";

const CheckedController = ({ image, selectionId, isSingleSelection, selectedItems, children }) => {
    const dispatch = useDrawerDispatch();

    const isChecked = useMemo(
        () => selectedItems?.some((selected) => selected.id === image.id) || false,
        [selectedItems, image.id],
    );

    const onClick = useCallback(() => {
        if (!selectionId) {
            return;
        }

        let items = selectedItems;

        if (isChecked) {
            items = isSingleSelection
                ? []
                : selectedItems.filter((selected) => selected.id !== image.id);
        } else {
            items = isSingleSelection ? [image] : [...selectedItems, image];
        }

        dispatch({
            type: DRAWER_ACTIONS.UPDATE_ITEMS,
            payload: {
                options: {
                    selectionId,
                    items,
                },
            },
        });
    }, [dispatch, image, selectedItems, isChecked, selectionId]);

    return cloneElement(Children.only(children), { image, checked: isChecked, onClick });
};

const UploaderGallery = ({ className, disableSearch, ...rest }) => {
    const uid = useUIDSeed();
    const dispatch = useDrawerDispatch();
    const { profile: user } = useUser();
    const [{ sort }] = useQueryState({ sort: { created_at: "desc" } });

    const { pages, ref, queryKey } = useListElastic({
        resource: "moj-profil",
        id: user?.id,
        endpoint: "slike",
        params: {
            sort,
        },
        enabled: !!user?.id,
        refetchOnMount: true,
    });

    const { selectionId, isSingleSelection = false, onSubmit, items } = useDrawerState();

    const selectedItems = useMemo(() => items[selectionId] || [], [items, selectionId]);

    const handleImageAddClick = (imageFile) => {
        dispatch({
            type: DRAWER_ACTIONS.OPEN_DRAWER,
            payload: {
                drawerType: drawerUID,
                options: {
                    content: imageFile,
                    isEditing: false,
                    queryKey,
                    gtmData: { action: "Photo", label: "Edit Photo" },
                },
            },
        });
    };

    const isSelectionEnabled = !!selectionId;

    return (
        <UploaderGalleryStyled className={className} {...rest}>
            {/* @TODO: Simple search here */}
            {/* <UploaderSearch disabled={disableSearch} /> */}
            <DrawerBody>
                <div className="uploaderGallery_grid">
                    <UploaderGalleryImageAdd onClick={handleImageAddClick} />
                    {pages?.map((images) =>
                        images.map((image, index) => {
                            if (image?.deleted) {
                                return null;
                            }

                            return (
                                <CheckedController
                                    key={uid(image.id || index)}
                                    image={image}
                                    selectionId={selectionId}
                                    isSingleSelection={isSingleSelection}
                                    selectedItems={selectedItems}
                                >
                                    <UploaderGalleryImage id={image?.id} name={image?.name} />
                                </CheckedController>
                            );
                        }),
                    )}
                </div>
                <InfiniteLoadTrigger ref={ref} />
            </DrawerBody>
            {isSelectionEnabled && (
                <DrawerFooter>
                    <Button
                        title={isSingleSelection ? "Odaberi sliku" : "Odaberi slike"}
                        type="button"
                        width="100%"
                        size={buttonSizes.LARGE}
                        variant={buttonVariants.QUATERNARY}
                        iconRightComponent={<Icon icon={CheckMarkIcon} />}
                        onClick={() => {
                            if (onSubmit) {
                                onSubmit(selectedItems); // onSubmit should handle closing when ready
                            } else {
                                dispatch({
                                    type: DRAWER_ACTIONS.CLOSE_DRAWER,
                                    payload: {
                                        drawerId: null,
                                    },
                                });
                            }
                        }}
                    >
                        {isSingleSelection ? "Odaberi sliku" : "Odaberi slike"}
                    </Button>
                </DrawerFooter>
            )}
            <Notification uid={NOTIFICATION_ID} />
        </UploaderGalleryStyled>
    );
};

UploaderGallery.propTypes = {
    disableSearch: PropTypes.bool,
    className: PropTypes.string,
};

UploaderGallery.defaultProps = {
    disableSearch: false,
    className: undefined,
};

export default withErrorBoundary(UploaderGallery);
